import React, { useState, useEffect, Suspense } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Header from "./layout-components/header";
import Sidebar from "./layout-components/sidebar";
import ThemeRoutes from "../routes/router";
import Spinner from "../views/spinner/spinner";
import { setHeadAttributes } from "../utils/common.util";
import { rememberReturnTo } from "../utils/return-to.util";

const FullLayout = ({
  component: Component,
  navlabel,
  redirect,
  pathTo,
  isAuthenticated,
  title = "Delete My Info",
  description = "",
}) => {
  const [width, setWidth] = useState(window.innerWidth);
  const settings = useSelector((state) => state.settings);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      const updateDimensions = () => {
        let element = document.getElementById("main-wrapper");
        setWidth(window.innerWidth);
        switch (settings.activeSidebarType) {
          case "full":
          case "iconbar":
            if (width < 1170) {
              element.setAttribute("data-sidebartype", "mini-sidebar");
              element.classList.add("mini-sidebar");
            } else {
              element.setAttribute(
                "data-sidebartype",
                settings.activeSidebarType,
              );
              element.classList.remove("mini-sidebar");
            }
            break;

          case "overlay":
            if (width < 767) {
              element.setAttribute("data-sidebartype", "mini-sidebar");
            } else {
              element.setAttribute(
                "data-sidebartype",
                settings.activeSidebarType,
              );
            }
            break;

          default:
        }
      };
      if (document.readyState === "complete") {
        updateDimensions();
      }
      window.addEventListener("load", updateDimensions.bind(null));
      window.addEventListener("resize", updateDimensions.bind(null));
      return () => {
        window.removeEventListener("load", updateDimensions.bind(null));
        window.removeEventListener("resize", updateDimensions.bind(null));
      };
    }
  }, [settings?.activeSidebarType, width]);

  useEffect(() => {
    setHeadAttributes(title, description);
  }, [title, description]);

  return (
    <div
      id="main-wrapper"
      dir={settings?.activeDir}
      data-theme={settings?.activeTheme}
      data-layout={settings?.activeThemeLayout}
      data-sidebartype={settings?.activeSidebarType}
      data-sidebar-position={settings?.activeSidebarPos}
      data-header-position={settings?.activeHeaderPos}
      data-boxed-layout={settings?.activeLayout}
    >
      {!isAuthenticated ? (
        // Remember the page asked for, so login returns here (see utils/return-to.util.js).
        <Navigate
          to="/auth/login"
          replace={true}
          state={{
            from: rememberReturnTo(`${location.pathname}${location.search}`),
          }}
        />
      ) : (
        <>
          <Header />
          <Sidebar routes={ThemeRoutes} />
          <div className="page-wrapper d-block">
            <div className="page-content container-fluid">
              <Suspense fallback={<Spinner />}>
                {navlabel ? null : redirect ? (
                  <Navigate to={pathTo} replace />
                ) : Component ? (
                  <Component />
                ) : (
                  <Navigate to="/dashboard" replace={true} />
                )}
              </Suspense>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

FullLayout.propTypes = {
  component: PropTypes.elementType,
  navlabel: PropTypes.bool,
  redirect: PropTypes.bool,
  pathTo: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default FullLayout;
